import { VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Grid } from '@mui/material';

import MenuBar, { MenuHeader, MenuSection } from '../../components/new-layout/MenuBar';
import PageLayout from '../../components/new-layout/PageLayout';
import * as routing from '../../constants/Routing';
import ConfirmDialog from '../../dialogs/ConfirmDialog';
import { useRetrieveUserCredentials } from '../../hooks/user/useRetrieveUserCredentials';
import Text from '../../react-components/Text';
import eventsPanel, { PageViewPages } from '../../services/events';
import { RegisterWhatsapp } from '../auth/register-whatsapp';
import { AccountProfile } from './account/AccountProfile';
import { Billing } from './billing/Billing';
import { BillingInvoices } from './billing/BillingInvoices';
import { ApiKeys } from './integration/keys/ApiKeys';
import Webhooks from './integration/webhooks/Webhooks';
import { OrganizationProfile } from './organization/OrganizationProfile';
import { OrganizationUsers } from './organization/OrganizationUsers';
import { OrganizationProjects } from './projects/OrganizationProjects';

export const Settings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const [dirtyState, setDirtyState] = useState<boolean>(false);
  const [routeSelected, setRouteSelected] = useState<string | undefined>(undefined);
  const [modalChangesNotSaved, setModalChangesNotSaved] = useState<boolean>(false);

  useEffect(() => {
    if (!!location && location.pathname !== ``) {
      eventsPanel.pageView(PageViewPages.settings, location.pathname);
    }
  }, [location]);

  const isDirtyState = () => dirtyState;

  const handleDirtyState = (dirtyStateChange: any) => setDirtyState(dirtyStateChange);

  const handleOpenCloseDialog = (newRouteSelected?: string) => {
    setModalChangesNotSaved(!modalChangesNotSaved);
    setRouteSelected(newRouteSelected);
  };

  if (!userCredentials) {
    return null;
  }

  return (
    <>
      <PageLayout>
        <>
          <MenuBar
            content={
              <div>
                <MenuHeader>
                  <Text variant="h6" intlId="settings.leftbar.title" />
                </MenuHeader>

                <MenuSection
                  id={routing.SETTINGS.ACCOUNT.ROOT}
                  active={location.pathname === routing.SETTINGS.ACCOUNT.PROFILE}
                  textIntlId="settings.leftbar.account"
                  textStyle={{ fontWeight: `bold` }}
                  onClick={() => {
                    if (isDirtyState()) {
                      handleOpenCloseDialog(routing.SETTINGS.ACCOUNT.ROOT);
                    } else {
                      navigate(routing.SETTINGS.ACCOUNT.ROOT);
                    }
                  }}
                />

                <MenuSection
                  id={routing.SETTINGS.ACCOUNT.PROFILE}
                  active={location.pathname === routing.SETTINGS.ACCOUNT.PROFILE}
                  textIntlId="settings.leftbar.account.profile"
                  indent={1}
                  onClick={() => {
                    if (isDirtyState()) {
                      handleOpenCloseDialog(routing.SETTINGS.ACCOUNT.PROFILE);
                    } else {
                      navigate(routing.SETTINGS.ACCOUNT.PROFILE);
                    }
                  }}
                />

                {userCredentials.test(`can_update_organization`) && (
                  <>
                    <VSpacer small />
                    <MenuSection
                      id={routing.SETTINGS.ORGANIZATION.ROOT}
                      active={
                        location.pathname === routing.SETTINGS.ORGANIZATION.PROFILE ||
                        location.pathname === routing.SETTINGS.ORGANIZATION.PROJECTS ||
                        location.pathname === routing.SETTINGS.ORGANIZATION.USERS
                      }
                      textIntlId="settings.leftbar.organization"
                      textStyle={{ fontWeight: `bold` }}
                      onClick={() => {
                        if (isDirtyState()) {
                          handleOpenCloseDialog(routing.SETTINGS.ORGANIZATION.ROOT);
                        } else {
                          navigate(routing.SETTINGS.ORGANIZATION.ROOT);
                        }
                      }}
                    />
                    <MenuSection
                      id={routing.SETTINGS.ORGANIZATION.PROFILE}
                      active={location.pathname === routing.SETTINGS.ORGANIZATION.PROFILE}
                      textIntlId="settings.leftbar.organization.profile"
                      indent={1}
                      onClick={() => {
                        if (isDirtyState()) {
                          handleOpenCloseDialog(routing.SETTINGS.ORGANIZATION.PROFILE);
                        } else {
                          navigate(routing.SETTINGS.ORGANIZATION.PROFILE);
                        }
                      }}
                    />

                    <MenuSection
                      id={routing.SETTINGS.ORGANIZATION.PROJECTS}
                      active={location.pathname === routing.SETTINGS.ORGANIZATION.PROJECTS}
                      textIntlId="settings.leftbar.organization.projects"
                      indent={1}
                      onClick={() => {
                        if (isDirtyState()) {
                          handleOpenCloseDialog(routing.SETTINGS.ORGANIZATION.PROJECTS);
                        } else {
                          navigate(routing.SETTINGS.ORGANIZATION.PROJECTS);
                        }
                      }}
                    />
                    <MenuSection
                      id={routing.SETTINGS.ORGANIZATION.USERS}
                      active={location.pathname === routing.SETTINGS.ORGANIZATION.USERS}
                      textIntlId="settings.leftbar.organization.users"
                      indent={1}
                      onClick={() => {
                        if (isDirtyState()) {
                          handleOpenCloseDialog(routing.SETTINGS.ORGANIZATION.USERS);
                        } else {
                          navigate(routing.SETTINGS.ORGANIZATION.USERS);
                        }
                      }}
                    />
                  </>
                )}

                <VSpacer small />

                {userCredentials.test(`can_manage_billing`) ? (
                  <>
                    <MenuSection
                      id={routing.SETTINGS.BILLING.ROOT}
                      active={
                        location.pathname === routing.SETTINGS.BILLING.SUBSCRIPTION ||
                        location.pathname === routing.SETTINGS.BILLING.INVOICES
                      }
                      textIntlId="settings.leftbar.billing"
                      textStyle={{ fontWeight: `bold` }}
                      onClick={() => {
                        if (isDirtyState()) {
                          handleOpenCloseDialog(routing.SETTINGS.BILLING.ROOT);
                        } else {
                          navigate(routing.SETTINGS.BILLING.ROOT);
                        }
                      }}
                    />

                    <MenuSection
                      id={routing.SETTINGS.BILLING.SUBSCRIPTION}
                      active={location.pathname === routing.SETTINGS.BILLING.SUBSCRIPTION}
                      indent={1}
                      textIntlId="settings.leftbar.billing.subscription"
                      onClick={() => {
                        if (isDirtyState()) {
                          handleOpenCloseDialog(routing.SETTINGS.BILLING.SUBSCRIPTION);
                        } else {
                          navigate(routing.SETTINGS.BILLING.SUBSCRIPTION);
                        }
                      }}
                    />

                    <MenuSection
                      id={routing.SETTINGS.BILLING.INVOICES}
                      active={location.pathname === routing.SETTINGS.BILLING.INVOICES}
                      indent={1}
                      textIntlId="settings.leftbar.billing.invoices"
                      onClick={() => {
                        if (isDirtyState()) {
                          handleOpenCloseDialog(routing.SETTINGS.BILLING.INVOICES);
                        } else {
                          navigate(routing.SETTINGS.BILLING.INVOICES);
                        }
                      }}
                    />

                    <VSpacer small />
                  </>
                ) : null}

                {userCredentials.test(`can_manage_developers`) ? (
                  <>
                    <MenuSection
                      id={routing.SETTINGS.DEVELOPERS.ROOT}
                      active={
                        location.pathname === routing.SETTINGS.DEVELOPERS.API_KEYS ||
                        location.pathname === routing.SETTINGS.DEVELOPERS.WEBHOOKS
                      }
                      textIntlId="settings.leftbar.developers"
                      textStyle={{ fontWeight: `bold` }}
                      onClick={() => {
                        if (isDirtyState()) {
                          handleOpenCloseDialog(routing.SETTINGS.DEVELOPERS.ROOT);
                        } else {
                          navigate(routing.SETTINGS.DEVELOPERS.ROOT);
                        }
                      }}
                    />

                    <MenuSection
                      id={routing.SETTINGS.DEVELOPERS.API_KEYS}
                      active={location.pathname === routing.SETTINGS.DEVELOPERS.API_KEYS}
                      textIntlId="settings.leftbar.developers.api_keys"
                      indent={1}
                      onClick={() => {
                        if (isDirtyState()) {
                          handleOpenCloseDialog(routing.SETTINGS.DEVELOPERS.API_KEYS);
                        } else {
                          navigate(routing.SETTINGS.DEVELOPERS.API_KEYS);
                        }
                      }}
                    />

                    <MenuSection
                      id={routing.SETTINGS.DEVELOPERS.WEBHOOKS}
                      active={location.pathname === routing.SETTINGS.DEVELOPERS.WEBHOOKS}
                      textIntlId="settings.leftbar.developers.webhooks"
                      indent={1}
                      onClick={() => {
                        if (isDirtyState()) {
                          handleOpenCloseDialog(routing.SETTINGS.DEVELOPERS.WEBHOOKS);
                        } else {
                          navigate(routing.SETTINGS.DEVELOPERS.WEBHOOKS);
                        }
                      }}
                    />
                  </>
                ) : null}
              </div>
            }
          />

          <Grid style={{ height: `100%`, width: `100%`, overflowY: `auto`, zIndex: 1 }}>
            <Routes>
              <Route
                path="/account/*"
                element={
                  <Routes>
                    <Route index element={<Navigate to={routing.SETTINGS.ACCOUNT.PROFILE} />} />
                    <Route path="/profile" element={<AccountProfile isDirtyState={handleDirtyState} />} />
                    <Route path="*" element={<Navigate to={routing.SETTINGS.ACCOUNT.PROFILE} />} />
                  </Routes>
                }
              />
              {userCredentials.test(`can_update_organization`) && (
                <Route
                  path="/organization/*"
                  element={
                    <Routes>
                      <Route index element={<Navigate to={routing.SETTINGS.ORGANIZATION.PROFILE} />} />
                      <Route path="/profile" element={<OrganizationProfile isDirtyState={handleDirtyState} />} />
                      <Route path="/projects" element={<OrganizationProjects />} />
                      <Route path="/users" element={<OrganizationUsers />} />
                      <Route path="/register-whatsapp" element={<RegisterWhatsapp />} />
                      <Route path="*" element={<Navigate to={routing.SETTINGS.ORGANIZATION.PROFILE} />} />
                    </Routes>
                  }
                />
              )}

              <Route
                path="/billing/*"
                element={
                  <Routes>
                    <Route index element={<Navigate to={routing.SETTINGS.BILLING.SUBSCRIPTION} />} />
                    <Route path="/subscription" element={<Billing />} />
                    {/* <Route path="/subscription-update" element={<BillingSubscriptionUpdateDialog isOpen onClose />} /> */}
                    <Route path="/invoices" element={<BillingInvoices />} />
                    <Route path="*" element={<Navigate to={routing.SETTINGS.BILLING.SUBSCRIPTION} />} />
                  </Routes>
                }
              />

              <Route
                path="/developers/*"
                element={
                  <Routes>
                    <Route index element={<Navigate to={routing.SETTINGS.DEVELOPERS.API_KEYS} />} />
                    <Route path="/api-keys" element={<ApiKeys />} />
                    <Route path="/webhooks" element={<Webhooks />} />
                    <Route path="*" element={<Navigate to={routing.SETTINGS.DEVELOPERS.API_KEYS} />} />
                  </Routes>
                }
              />

              <Route path="*" element={<Navigate to={routing.SETTINGS.ACCOUNT.PROFILE} />} />
            </Routes>
          </Grid>
        </>
      </PageLayout>

      <ConfirmDialog
        open={dirtyState && modalChangesNotSaved}
        onCloseDialog={handleOpenCloseDialog}
        title={<Text variant="h6" intlId="settings.notsaved.title" />}
        description={<Text intlId="settings.notsaved.subtitle" />}
        cancelIntlId="settings.notsaved.stayonpage"
        confirmIntlId="settings.notsaved.leavethepage"
        onConfirm={() => {
          if (routeSelected) {
            navigate(routeSelected);
            handleOpenCloseDialog();
            handleDirtyState(false);
          }
        }}
      />
    </>
  );
};
